<template>
    <NegociosShell>
        <section class="sp-section sp-registro" :class="{ 'sp-registro--exito': creada }">
            <!-- Orbes de luz: los mismos de la landing y el login, para que el
                 fondo no sea un gris plano con una tarjeta encima. -->
            <div class="sp-glow-orb sp-glow-orb--orange" style="top: 4%; right: -6%; width: 420px; height: 420px;"></div>
            <div class="sp-glow-orb sp-glow-orb--cyan" style="bottom: 6%; left: -8%; width: 340px; height: 340px;"></div>

            <div class="sp-container">

                <!-- Cuenta creada -->
                <div v-if="creada" class="sp-exito sp-card">
                    <span class="sp-exito__icono" aria-hidden="true">
                        <i class="pi pi-check"></i>
                    </span>

                    <h1 class="sp-h2">¡Tu cuenta fue creada!</h1>

                    <p class="sp-exito__intro">
                        Registramos
                        <strong>{{ (creado && (creado.comercial || creado.negocio)) || 'tu negocio' }}</strong>
                        <template v-if="creado && creado.ciudad"> en {{ creado.ciudad }}</template>
                        junto con su primera sede.
                    </p>

                    <p v-if="requiereVerificacion" class="sp-exito__aviso">
                        <i class="pi pi-envelope" aria-hidden="true"></i>
                        <span>
                            Te enviamos un código de 6 dígitos a
                            <strong>{{ creado && creado.correo }}</strong> para activar tu cuenta.
                        </span>
                    </p>

                    <!-- Que sigue: son los pasos reales del proceso, no promesas. -->
                    <ol class="sp-exito__pasos">
                        <li>
                            <span class="sp-exito__num">1</span>
                            <span>Confirma tu correo con el código que te enviamos.</span>
                        </li>
                        <li>
                            <span class="sp-exito__num">2</span>
                            <span>Seway valida tu negocio y define el área de cobertura de tu sede.</span>
                        </li>
                        <li>
                            <span class="sp-exito__num">3</span>
                            <span>Publica tu primera oferta desde el portal de anunciantes.</span>
                        </li>
                    </ol>

                    <button type="button" class="sp-btn sp-btn--primary sp-exito__cta" @click="irAlPortal">
                        <span>{{ requiereVerificacion ? 'Confirmar mi correo' : 'Ingresar a Seway Plus Negocios' }}</span>
                        <i class="pi pi-arrow-right" aria-hidden="true"></i>
                    </button>

                    <p class="sp-exito__pie">
                        También puedes entrar más tarde desde
                        <router-link to="/negocios/login">iniciar sesión</router-link>
                        con tu correo y contraseña.
                    </p>
                </div>

                <template v-else>
                    <div class="sp-section-head">
                        <span class="sp-eyebrow">Anunciantes</span>
                        <h1 class="sp-h2">Crea tu cuenta de anunciante</h1>
                        <p class="sp-lead">
                            Registra tu negocio y empieza a conectar con conductores que circulan cerca de
                            tu establecimiento.
                        </p>
                    </div>

                    <!-- Indicador de pasos: barra en escritorio, texto en móvil -->
                    <div class="sp-pasos-nav">
                        <!-- Indicador propio en lugar de <Steps> de PrimeVue: en la
                             version 3.17 ese componente decide el paso activo comparando
                             RUTAS (isActive), no admite activeIndex, y por eso ningun paso
                             se resaltaba nunca en un wizard por indice. -->
                        <ol class="sp-wizard" aria-label="Progreso del registro">
                            <li
                                v-for="(item, i) in modeloPasos"
                                :key="item.label"
                                class="sp-wizard__paso"
                                :class="{ 'is-completado': i < indiceVisible, 'is-actual': i === indiceVisible }"
                                :aria-current="i === indiceVisible ? 'step' : null"
                            >
                                <span class="sp-wizard__circulo">
                                    <i v-if="i < indiceVisible" class="pi pi-check" aria-hidden="true"></i>
                                    <template v-else>{{ i + 1 }}</template>
                                </span>
                                <span class="sp-wizard__etiqueta">{{ item.label }}</span>
                                <span class="sp-visually-hidden">
                                    {{ i < indiceVisible ? '(completado)' : (i === indiceVisible ? '(paso actual)' : '(pendiente)') }}
                                </span>
                            </li>
                        </ol>
                        <p class="sp-pasos-movil" role="status">
                            Paso {{ indiceVisible + 1 }} de 3 · <strong>{{ modeloPasos[indiceVisible].label }}</strong>
                        </p>
                    </div>

                    <form class="sp-card sp-registro__form" novalidate @submit.prevent="siguiente">
                        <PasoCuenta
                            v-show="paso === 0"
                            :datos="formulario.cuenta"
                            :errores="errores"
                            :tipos-identificacion="tiposIdentificacion"
                            :cargando-catalogos="cargandoCatalogos"
                            @google="entrarConGoogle"
                            @ir-login="irALogin"
                        />

                        <PasoNegocio
                            v-show="paso === 1"
                            :datos="formulario.negocio"
                            :errores="errores"
                            :categorias="categorias"
                            :divipola="divipola"
                            :cargando-catalogos="cargandoCatalogos"
                        />

                        <PasoFacturacion
                            v-show="paso === 2"
                            :datos="formulario.facturacion"
                            :legal="formulario.legal"
                            :negocio="formulario.negocio"
                            :errores="errores"
                            :tipos-identificacion="tiposIdentificacion"
                            :divipola="divipola"
                            :rut-nombre="rutNombre"
                            @rut="alSeleccionarRut"
                        />

                        <PasoResumen
                            v-if="paso === 3"
                            :cuenta="formulario.cuenta"
                            :negocio="formulario.negocio"
                            :facturacion="formulario.facturacion"
                            :legal="formulario.legal"
                            :categorias="categorias"
                            :divipola="divipola"
                            :rut-nombre="rutNombre"
                            @editar="irAPaso"
                        />

                        <div class="sp-registro__acciones">
                            <button
                                v-if="paso > 0"
                                type="button"
                                class="sp-btn sp-btn--outline"
                                :disabled="enviando"
                                @click="atras"
                            >
                                <i class="pi pi-arrow-left" aria-hidden="true"></i>
                                <span>Atrás</span>
                            </button>

                            <button type="submit" class="sp-btn sp-btn--primary sp-registro__avanzar" :disabled="enviando">
                                <i v-if="enviando" class="pi pi-spin pi-spinner" aria-hidden="true"></i>
                                <span>{{ textoBotonPrincipal }}</span>
                                <i v-if="!enviando" class="pi pi-arrow-right" aria-hidden="true"></i>
                            </button>
                        </div>

                        <p class="sp-registro__login">
                            ¿Ya tienes una cuenta?
                            <a href="#" @click.prevent="irALogin">Iniciar sesión</a>
                        </p>
                    </form>
                </template>

            </div>
        </section>
    </NegociosShell>
</template>

<script>
import NegociosShell from '../NegociosShell';
import PasoCuenta from './pasos/PasoCuenta';
import PasoNegocio from './pasos/PasoNegocio';
import PasoFacturacion from './pasos/PasoFacturacion';
import PasoResumen from './pasos/PasoResumen';
import api, { mensajeDeError, erroresDeCampo } from './api';
import { iniciarSesion, estaAutenticado } from './sesion';
import { cargarDivipola, cargarTiposIdentificacion, cargarCategorias, departamentoDeId } from './catalogos';
import { registrarEvento, EVENTOS } from './analitica';
import { aplicarSeo, limpiarSeo } from '../seo';

const CLAVE_BORRADOR = 'sp-registro-negocio';

function formularioVacio() {
    return {
        cuenta: {
            nombre: '',
            email: '',
            celular: '',
            id_tipo_identificacion: null,
            documento: '',
            password: '',
            password_confirmation: '',
            google_token: ''
        },
        negocio: {
            tipo_persona: 'JURIDICA',
            razon_social: '',
            nombre_comercial: '',
            nit: '',
            digito_verificacion: '',
            telefono: '',
            direccion: '',
            departamento: '',
            id_divipola: null,
            id_categoria_negocio: null
        },
        facturacion: {
            usa_datos_negocio: true,
            tipo_persona: 'JURIDICA',
            razon_social: '',
            id_tipo_identificacion: null,
            numero_identificacion: '',
            digito_verificacion: '',
            email_facturacion: '',
            direccion: '',
            departamento: '',
            id_divipola: null
        },
        legal: {
            acepto_terminos: false,
            acepto_privacidad: false
        }
    };
}

export default {
    name: 'RegistroAnunciante',
    components: { NegociosShell, PasoCuenta, PasoNegocio, PasoFacturacion, PasoResumen },
    data() {
        return {
            paso: 0,
            enviando: false,
            creada: false,
            requiereVerificacion: false,
            // Se guarda lo que devuelve el registro para poder resumir en la
            // pantalla de exito QUE quedo creado, no solo que "fue creado".
            creado: null,
            errores: {},
            formulario: formularioVacio(),
            // El File nunca se serializa: solo su nombre sobrevive a un refresco.
            rutArchivo: null,
            rutNombre: '',
            divipola: [],
            tiposIdentificacion: [],
            categorias: [],
            cargandoCatalogos: true,
            modeloPasos: [{ label: 'Tu cuenta' }, { label: 'Tu negocio' }, { label: 'Facturación' }]
        };
    },
    computed: {
        indiceVisible() {
            return Math.min(this.paso, 2);
        },
        ciudadElegida() {
            const id = this.formulario.negocio.id_divipola;
            if (!id) return '';
            const f = this.divipola.filter((x) => Number(x.id) === Number(id))[0];
            return f ? f.municipio + ', ' + f.departamento : '';
        },
        textoBotonPrincipal() {
            if (this.enviando) return 'Creando cuenta…';
            if (this.paso === 3) return 'Crear cuenta';
            if (this.paso === 2) return 'Revisar y continuar';
            return 'Continuar';
        }
    },
    watch: {
        formulario: {
            deep: true,
            handler() {
                this.guardarBorrador();
            }
        },
        // Persona natural: se reutiliza el nombre del titular del paso 1.
        'formulario.negocio.tipo_persona'(valor) {
            if (valor === 'NATURAL' && !this.formulario.negocio.razon_social) {
                this.formulario.negocio.razon_social = this.formulario.cuenta.nombre;
            }
        }
    },
    mounted() {
        aplicarSeo({
            titulo: 'Crear cuenta de anunciante | Seway Plus Negocios',
            descripcion:
                'Registra tu negocio en Seway Plus y empieza a mostrar tus ofertas a conductores de carga que circulan cerca de tu establecimiento.',
            imagen: 'layout/images/hero-truck-bg.png'
        });

        registrarEvento(EVENTOS.VER);

        if (estaAutenticado()) {
            this.$router.replace({ path: '/negocios/portal' });
            return;
        }

        this.recuperarBorrador();
        this.cargarCatalogos();
    },
    unmounted() {
        limpiarSeo();
    },
    methods: {
        /* ---------------------------------------------------- catálogos */
        cargarCatalogos() {
            this.cargandoCatalogos = true;

            Promise.all([cargarDivipola(), cargarTiposIdentificacion(), cargarCategorias()])
                .then(([divipola, tipos, categorias]) => {
                    this.divipola = divipola;
                    this.tiposIdentificacion = tipos;
                    this.categorias = categorias;

                    // Rehidrata el departamento a partir del municipio guardado.
                    if (this.formulario.negocio.id_divipola && !this.formulario.negocio.departamento) {
                        this.formulario.negocio.departamento = departamentoDeId(
                            divipola,
                            this.formulario.negocio.id_divipola
                        );
                    }
                    if (this.formulario.facturacion.id_divipola && !this.formulario.facturacion.departamento) {
                        this.formulario.facturacion.departamento = departamentoDeId(
                            divipola,
                            this.formulario.facturacion.id_divipola
                        );
                    }
                })
                .finally(() => {
                    this.cargandoCatalogos = false;
                });
        },

        /* -------------------------------------------------- persistencia */
        guardarBorrador() {
            try {
                const copia = JSON.parse(JSON.stringify(this.formulario));
                // La contraseña NUNCA se guarda en sessionStorage.
                copia.cuenta.password = '';
                copia.cuenta.password_confirmation = '';
                window.sessionStorage.setItem(CLAVE_BORRADOR, JSON.stringify({ paso: this.paso, formulario: copia }));
            } catch (e) {
                // sessionStorage puede estar deshabilitado; no es crítico.
            }
        },
        recuperarBorrador() {
            try {
                const crudo = window.sessionStorage.getItem(CLAVE_BORRADOR);
                if (!crudo) return;

                const guardado = JSON.parse(crudo);
                if (guardado && guardado.formulario) {
                    const base = formularioVacio();
                    this.formulario = {
                        cuenta: Object.assign(base.cuenta, guardado.formulario.cuenta || {}),
                        negocio: Object.assign(base.negocio, guardado.formulario.negocio || {}),
                        facturacion: Object.assign(base.facturacion, guardado.formulario.facturacion || {}),
                        legal: Object.assign(base.legal, guardado.formulario.legal || {})
                    };
                    // El resumen no se restaura: siempre se vuelve a un paso editable.
                    this.paso = Math.min(guardado.paso || 0, 2);
                }
            } catch (e) {
                window.sessionStorage.removeItem(CLAVE_BORRADOR);
            }
        },
        limpiarBorrador() {
            try {
                window.sessionStorage.removeItem(CLAVE_BORRADOR);
            } catch (e) {
                // sin efecto
            }
        },

        /* ---------------------------------------------------- navegación */
        irAPaso(indice) {
            this.errores = {};
            this.paso = indice;
            this.subirArriba();
        },
        atras() {
            this.errores = {};
            this.paso = Math.max(0, this.paso - 1);
            this.subirArriba();
        },
        siguiente() {
            if (this.paso === 3) {
                this.crearCuenta();
                return;
            }

            if (!this.validarPaso(this.paso)) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Revisa los campos',
                    detail: 'Faltan datos obligatorios en este paso.',
                    life: 4000
                });
                return;
            }

            registrarEvento([EVENTOS.PASO_1, EVENTOS.PASO_2, EVENTOS.PASO_3][this.paso]);

            // Al entrar a facturación se propone el correo de la cuenta.
            if (this.paso === 1 && !this.formulario.facturacion.email_facturacion) {
                this.formulario.facturacion.email_facturacion = this.formulario.cuenta.email;
            }

            this.errores = {};
            this.paso += 1;
            this.subirArriba();
        },
        subirArriba() {
            const reducido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            window.scrollTo({ top: 0, behavior: reducido ? 'auto' : 'smooth' });
        },
        irALogin() {
            registrarEvento(EVENTOS.LOGIN_DESDE_REGISTRO);
            this.$router.push({ path: '/negocios/login' });
        },
        irAlPortal() {
            this.$router.push({ path: '/negocios/portal' });
        },

        /* ---------------------------------------------------- validación */
        validarPaso(indice) {
            const e = {};
            const { cuenta, negocio, facturacion, legal } = this.formulario;

            if (indice === 0) {
                if (!cuenta.nombre) e.nombre = 'Ingresa tu nombre completo.';
                if (!cuenta.email) e.email = 'Ingresa tu correo electrónico.';
                else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(cuenta.email)) e.email = 'El correo no es válido.';
                if (!cuenta.celular || cuenta.celular.replace(/\D/g, '').length < 7) e.celular = 'Ingresa un teléfono válido.';
                if (!cuenta.id_tipo_identificacion) e.id_tipo_identificacion = 'Selecciona el tipo de documento.';
                if (!cuenta.documento) e.documento = 'Ingresa tu número de documento.';

                if (!cuenta.google_token) {
                    if (!cuenta.password) e.password = 'Ingresa una contraseña.';
                    else if (cuenta.password.length < 8) e.password = 'La contraseña debe tener al menos 8 caracteres.';
                    if (!cuenta.password_confirmation) e.password_confirmation = 'Confirma la contraseña.';
                    else if (cuenta.password !== cuenta.password_confirmation) {
                        e.password_confirmation = 'Las contraseñas no coinciden.';
                    }
                }
            }

            if (indice === 1) {
                if (!negocio.razon_social) e.razon_social = 'Este campo es obligatorio.';
                if (negocio.tipo_persona === 'JURIDICA' && !negocio.nit) e.nit = 'Ingresa el NIT del negocio.';
                if (!negocio.id_categoria_negocio) e.id_categoria_negocio = 'Selecciona la categoría.';
                if (!negocio.direccion) e.direccion = 'Ingresa la dirección del negocio.';
                if (!negocio.departamento) e.departamento = 'Selecciona el departamento.';
                if (!negocio.id_divipola) e.id_divipola = 'Selecciona la ciudad.';
            }

            if (indice === 2) {
                if (!facturacion.email_facturacion) e.email_facturacion = 'Ingresa el correo de facturación.';
                else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(facturacion.email_facturacion)) {
                    e.email_facturacion = 'El correo no es válido.';
                }

                if (!facturacion.usa_datos_negocio) {
                    if (!facturacion.razon_social) e.razon_social = 'Este campo es obligatorio.';
                    if (!facturacion.numero_identificacion) e.numero_identificacion = 'Ingresa la identificación.';
                    if (!facturacion.direccion) e.direccion = 'Ingresa la dirección de facturación.';
                    if (!facturacion.departamento) e.departamento = 'Selecciona el departamento.';
                    if (!facturacion.id_divipola) e.id_divipola = 'Selecciona el municipio.';
                }

                if (!legal.acepto_terminos) e.acepto_terminos = 'Debes aceptar los términos.';
                if (!legal.acepto_privacidad) e.acepto_privacidad = 'Debes aceptar la política de privacidad.';
            }

            this.errores = e;
            return Object.keys(e).length === 0;
        },

        /* --------------------------------------------------------- Google */
        entrarConGoogle(idToken) {
            this.enviando = true;

            api.post('negocios/google', { id_token: idToken })
                .then((respuesta) => {
                    const cuerpo = respuesta.data || {};
                    const datos = cuerpo.data || {};

                    if (datos.existe) {
                        // Ya tenía cuenta: se inicia sesión directamente.
                        iniciarSesion(cuerpo.access_token, cuerpo.anunciante);
                        this.limpiarBorrador();
                        this.$router.push({ path: '/negocios/portal' });
                        return;
                    }

                    // No existe: se prellena el paso 1 con lo que da Google.
                    const perfil = datos.perfil || {};
                    this.formulario.cuenta.nombre = perfil.nombre || this.formulario.cuenta.nombre;
                    this.formulario.cuenta.email = perfil.email || this.formulario.cuenta.email;
                    this.formulario.cuenta.google_token = idToken;
                    this.formulario.cuenta.password = '';
                    this.formulario.cuenta.password_confirmation = '';

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Cuenta de Google verificada',
                        detail: 'Completa los datos que faltan para continuar.',
                        life: 4000
                    });
                })
                .catch((error) => {
                    this.$toast.add({
                        severity: 'error',
                        summary: 'No pudimos usar tu cuenta de Google',
                        detail: mensajeDeError(error),
                        life: 5000
                    });
                })
                .finally(() => {
                    this.enviando = false;
                });
        },

        /* ------------------------------------------------------------ RUT */
        alSeleccionarRut(archivo) {
            this.rutArchivo = archivo;
            this.rutNombre = archivo ? archivo.name : '';
        },
        subirRut() {
            if (!this.rutArchivo) return Promise.resolve();

            const datos = new FormData();
            datos.append('rut', this.rutArchivo);

            return api
                .post('negocios/rut', datos)
                .then(() => {
                    registrarEvento(EVENTOS.RUT);
                })
                .catch(() => {
                    // La cuenta ya existe; el RUT puede cargarse luego desde el perfil.
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Cuenta creada, pero el RUT no se cargó',
                        detail: 'Puedes adjuntarlo más tarde desde tu perfil.',
                        life: 6000
                    });
                });
        },

        /* -------------------------------------------------- crear cuenta */
        crearCuenta() {
            // Se revalidan los 3 pasos, no solo el último.
            for (let i = 0; i <= 2; i++) {
                if (!this.validarPaso(i)) {
                    this.paso = i;
                    this.subirArriba();
                    this.$toast.add({
                        severity: 'warn',
                        summary: 'Falta información',
                        detail: 'Revisa los campos marcados.',
                        life: 4000
                    });
                    return;
                }
            }

            this.enviando = true;
            registrarEvento(EVENTOS.ENVIAR);
            registrarEvento(EVENTOS.TERMINOS);

            const { cuenta, negocio, facturacion } = this.formulario;

            const cuerpo = {
                cuenta: {
                    nombre: cuenta.nombre,
                    email: cuenta.email,
                    celular: this.soloDigitos(cuenta.celular),
                    id_tipo_identificacion: cuenta.id_tipo_identificacion,
                    documento: this.soloDigitos(cuenta.documento)
                },
                negocio: {
                    tipo_persona: negocio.tipo_persona,
                    razon_social: negocio.razon_social,
                    nombre_comercial: negocio.nombre_comercial,
                    nit: this.soloDigitos(negocio.nit),
                    digito_verificacion: negocio.digito_verificacion,
                    telefono: this.soloDigitos(negocio.telefono),
                    direccion: negocio.direccion,
                    id_divipola: negocio.id_divipola,
                    id_categoria_negocio: negocio.id_categoria_negocio
                },
                facturacion: {
                    usa_datos_negocio: facturacion.usa_datos_negocio,
                    email_facturacion: facturacion.email_facturacion,
                    tipo_persona: facturacion.tipo_persona,
                    razon_social: facturacion.razon_social,
                    id_tipo_identificacion: facturacion.id_tipo_identificacion,
                    numero_identificacion: this.soloDigitos(facturacion.numero_identificacion),
                    digito_verificacion: facturacion.digito_verificacion,
                    direccion: facturacion.direccion,
                    id_divipola: facturacion.id_divipola
                },
                acepto_terminos: true,
                acepto_privacidad: true
            };

            if (cuenta.google_token) {
                cuerpo.google_token = cuenta.google_token;
            } else {
                cuerpo.cuenta.password = cuenta.password;
                cuerpo.cuenta.password_confirmation = cuenta.password_confirmation;
            }

            api.post('negocios/registro', cuerpo)
                .then((respuesta) => {
                    const datos = respuesta.data || {};
                    iniciarSesion(datos.access_token, datos.anunciante);
                    this.requiereVerificacion = !!datos.requiere_verificacion;
                    this.creado = {
                        correo: (datos.anunciante && datos.anunciante.email) || this.formulario.cuenta.email,
                        negocio: (datos.negocio && datos.negocio.razon_social) || this.formulario.negocio.razon_social,
                        comercial: this.formulario.negocio.nombre_comercial,
                        ciudad: this.ciudadElegida
                    };

                    registrarEvento(EVENTOS.EXITO);

                    return this.subirRut().then(() => {
                        this.creada = true;
                        this.limpiarBorrador();
                        this.subirArriba();
                    });
                })
                .catch((error) => {
                    registrarEvento(EVENTOS.FALLO);
                    const estado = error && error.response ? error.response.status : 0;

                    if (estado === 409) {
                        this.paso = 0;
                        this.errores = { email: 'Este correo ya está asociado a una cuenta.' };
                        this.$toast.add({
                            severity: 'warn',
                            summary: 'Este correo ya tiene cuenta',
                            detail: 'Inicia sesión o usa otro correo para continuar.',
                            life: 7000
                        });
                        this.subirArriba();
                        return;
                    }

                    if (estado === 422) {
                        // El backend indexa por "cuenta.email"; se aplana la clave.
                        const crudos = erroresDeCampo(error);
                        const planos = {};
                        Object.keys(crudos).forEach((clave) => {
                            planos[clave.split('.').pop()] = crudos[clave];
                        });
                        this.errores = planos;
                    }

                    this.$toast.add({
                        severity: 'error',
                        summary: 'No pudimos crear tu cuenta',
                        detail: mensajeDeError(error),
                        life: 6000
                    });
                })
                .finally(() => {
                    this.enviando = false;
                });
        },

        soloDigitos(valor) {
            if (!valor) return '';
            return String(valor).replace(/\D/g, '');
        }
    }
};
</script>

<style lang="scss" scoped>
.sp-registro {
    background: var(--sp-surface-alt);
    min-height: 70vh;
}

.sp-pasos-nav {
    max-width: 820px;
    margin: 0 auto 1.75rem;
}

.sp-wizard {
    display: none;
}

.sp-pasos-movil {
    color: var(--sp-text-soft);
    font-size: 0.9rem;
    text-align: center;

    strong {
        color: var(--sp-navy);
    }
}

.sp-registro__form {
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    gap: 1.75rem;
    /* Antes 760px fijos: en un 27" el contenedor mide 1760px y la tarjeta
       dejaba 1000px de aire a los lados. */
    max-width: 820px;
    margin: 0 auto;

    /* Filo superior en degradado, igual que en el login. */
    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, var(--sp-orange) 0%, var(--sp-orange-400) 55%, var(--sp-navy) 100%);
    }

    /* Campos comodos: en esta pantalla el formulario ES el contenido.
       En variables para que el tramo de pantallas grandes no use el atajo
       `padding` y pise el hueco de los iconos. */
    --sp-inp-x: 1.15rem;
    --sp-inp-y: 0.95rem;

    :deep(.p-inputtext),
    :deep(.p-inputtextarea) {
        padding: var(--sp-inp-y) var(--sp-inp-x);
        font-size: 1.08rem;
        border-radius: 14px;
    }

    :deep(.p-dropdown) {
        border-radius: 14px;
    }

    :deep(.p-dropdown-label) {
        padding: var(--sp-inp-y) var(--sp-inp-x);
        font-size: 1.08rem;
    }

    :deep(.sp-campo label) {
        font-size: 1rem;
    }

    :deep(.sp-campo__ayuda) {
        font-size: 0.88rem;
    }
}

/* Encabezado de cada paso: estaba en 23px de titulo y 13px de texto. */
:deep(.sp-paso__head) {
    h2 {
        font-size: 1.55rem;
    }

    p {
        font-size: 1.05rem;
    }

    .sp-icon-badge {
        width: 4rem;
        height: 4rem;
        font-size: 1.6rem;
    }
}

.sp-registro {
    position: relative;
    overflow: hidden;
}

.sp-registro__acciones {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--sp-border);
}

.sp-registro__avanzar {
    flex: 1 1 auto;
}

.sp-registro__login {
    color: var(--sp-text-soft);
    font-size: 0.88rem;
    text-align: center;

    a {
        color: var(--sp-orange);
        font-weight: 700;
        text-decoration: underline;
    }
}

.sp-exito {
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.1rem;
    /* Antes 620px con contenido corto: la tarjeta quedaba flotando en medio de
       mucho vacio. Ahora es mas ancha y trae resumen y proximos pasos. */
    max-width: 720px;
    margin: 0 auto;
    padding: clamp(2.25rem, 4vw, 3.5rem);
    text-align: center;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: linear-gradient(90deg, var(--sp-orange) 0%, var(--sp-orange-400) 55%, var(--sp-navy) 100%);
    }
}

.sp-exito__intro {
    max-width: 46ch;
    color: var(--sp-text-soft);
    font-size: 1.12rem;
    line-height: 1.6;

    strong {
        color: var(--sp-navy);
    }
}

/* Icono redondo con anillo: es el momento de confirmacion de la pantalla, el
   cuadradito tenue de antes no lo sostenia. */
.sp-exito__icono {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 5.4rem;
    height: 5.4rem;
    background: linear-gradient(135deg, var(--sp-orange) 0%, var(--sp-orange-400) 100%);
    border-radius: 50%;
    color: #fff;
    font-size: 2.4rem;
    box-shadow: 0 10px 30px rgba(235, 93, 27, 0.35),
        0 0 0 10px rgba(235, 93, 27, 0.12);
}

.sp-exito__pasos {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
    width: 100%;
    margin: 0.5rem 0 0;
    padding: 1.35rem 1.5rem;
    background: var(--sp-surface-alt);
    border: 1px solid var(--sp-border);
    border-radius: var(--sp-radius);
    list-style: none;
    text-align: left;

    li {
        display: flex;
        align-items: flex-start;
        gap: 0.8rem;
        color: var(--sp-text);
        font-size: 1rem;
        line-height: 1.5;
    }
}

.sp-exito__num {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.85rem;
    height: 1.85rem;
    background: var(--sp-surface);
    border: 2px solid var(--sp-orange);
    border-radius: 50%;
    color: var(--sp-orange);
    font-size: 0.9rem;
    font-weight: 800;
}

.sp-exito__cta {
    margin-top: 0.5rem;
    min-height: 58px;
    font-size: 1.12rem;
}

.sp-exito__pie {
    color: var(--sp-text-soft);
    font-size: 0.95rem;

    a {
        color: var(--sp-enlace);
        font-weight: 700;

        &:hover { text-decoration: underline; }
    }
}

.sp-exito__aviso {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.9rem 1.1rem;
    background: var(--sp-orange-50);
    border-left: 4px solid var(--sp-orange);
    border-radius: 0 10px 10px 0;
    color: var(--sp-navy) !important;
    font-size: 1rem;
    text-align: left;
    width: 100%;

    i {
        flex: 0 0 auto;
        margin-top: 0.15rem;
        color: var(--sp-orange);
    }

    strong {
        overflow-wrap: anywhere;
    }
}

/* La barra de pasos solo desde tablet: en móvil ocupa demasiado y se corta */
@media (min-width: 768px) {

    .sp-pasos-movil {
        display: none;
    }

    .sp-registro__acciones {
        justify-content: flex-end;
    }

    .sp-registro__avanzar {
        flex: 0 0 auto;
    }
}


/* --------------------------------------------------------------------------
   Monitores grandes: sin esto la tarjeta se quedaba en 760px dentro de un
   contenedor de 1760px, con los campos en 13px.
   -------------------------------------------------------------------------- */
@media (min-width: 2000px) {
    .sp-registro__form {
        max-width: 980px;
        padding: 3.4rem;
        --sp-inp-x: 1.3rem;
        --sp-inp-y: 1.1rem;

        :deep(.p-inputtext),
        :deep(.p-inputtextarea),
        :deep(.p-dropdown-label) {
            font-size: 1.18rem;
        }

        :deep(.sp-campo label) {
            font-size: 1.08rem;
        }
    }

    :deep(.sp-paso__head) {
        h2 { font-size: 1.9rem; }
        p { font-size: 1.2rem; }

        .sp-icon-badge {
            width: 4.6rem;
            height: 4.6rem;
            font-size: 1.9rem;
        }
    }

    .sp-pasos-nav {
        max-width: 980px;
    }


    .sp-registro__avanzar {
        min-height: 60px;
        font-size: 1.2rem;
    }
}

/* --------------------------------------------------------------------------
   Indicador de pasos propio
   -------------------------------------------------------------------------- */
.sp-wizard {
    display: none;
    align-items: flex-start;
    gap: 0;
    margin: 0;
    padding: 0;
    list-style: none;
    counter-reset: none;
}

.sp-wizard__paso {
    position: relative;
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.6rem;
    text-align: center;

    /* Conector hasta el paso siguiente */
    &:not(:last-child)::after {
        content: '';
        position: absolute;
        top: 1.4rem;
        left: calc(50% + 1.9rem);
        width: calc(100% - 3.8rem);
        border-top: 2px solid var(--sp-border);
        transition: border-color var(--sp-transition-normal);
    }

    &.is-completado::after {
        border-top-color: var(--sp-orange);
    }
}

.sp-wizard__circulo {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.8rem;
    height: 2.8rem;
    background: var(--sp-surface);
    border: 2px solid var(--sp-border);
    border-radius: 50%;
    color: var(--sp-text-soft);
    font-size: 1.1rem;
    font-weight: 800;
    transition: background var(--sp-transition-fast), color var(--sp-transition-fast),
        border-color var(--sp-transition-fast), box-shadow var(--sp-transition-fast);
}

.sp-wizard__etiqueta {
    color: var(--sp-text-soft);
    font-size: 1.02rem;
    font-weight: 600;
}

/* Pasos ya superados: naranja plano con visto. */
.sp-wizard__paso.is-completado {
    .sp-wizard__circulo {
        background: var(--sp-orange);
        border-color: var(--sp-orange);
        color: #fff;
    }

    .sp-wizard__etiqueta {
        color: var(--sp-navy);
    }
}

/* Paso actual: degradado con halo, para localizarlo de inmediato. */
.sp-wizard__paso.is-actual {
    .sp-wizard__circulo {
        background: linear-gradient(135deg, var(--sp-orange) 0%, var(--sp-orange-400) 100%);
        border-color: transparent;
        color: #fff;
        box-shadow: 0 6px 18px rgba(235, 93, 27, 0.35);
    }

    .sp-wizard__etiqueta {
        color: var(--sp-navy);
        font-weight: 800;
    }
}

@media (min-width: 768px) {
    .sp-wizard {
        display: flex;
    }
}

@media (min-width: 2000px) {
    .sp-wizard__circulo {
        width: 3.3rem;
        height: 3.3rem;
        font-size: 1.3rem;
    }

    .sp-wizard__etiqueta {
        font-size: 1.18rem;
    }

    .sp-wizard__paso:not(:last-child)::after {
        top: 1.65rem;
        left: calc(50% + 2.2rem);
        width: calc(100% - 4.4rem);
    }
}

/* Con la tarjeta de exito el contenido es corto: se centra en el pliegue para
   que no quede arriba con un hueco grande debajo. */
@media (min-width: 992px) and (min-height: 700px) {
    .sp-registro--exito {
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-height: calc(100vh - var(--sp-header-h) - 6rem);
    }
}

@media (min-width: 2000px) {
    .sp-exito {
        max-width: 840px;
    }

    .sp-exito__intro { font-size: 1.28rem; }

    .sp-exito__pasos li { font-size: 1.12rem; }

    .sp-exito__icono {
        width: 6.2rem;
        height: 6.2rem;
        font-size: 2.8rem;
    }
}
</style>
